import React from 'react';
import {Box} from '../Box/Box';
import type {BoxProps} from '../Box/Box';
import {useIsFocusVisible, useForkRef} from '@mui/material/utils';
import {styled} from '@mui/material/styles';
import {excludeComponentProps} from '../utils/excludeComponentProps';

export interface TabPanelProps extends Pick<BoxProps, 'sx' | 'children'> {
    onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
    focusVisible?: boolean;
}

const StyledTabPanel = styled(Box, {
    ...excludeComponentProps('focusVisible')
})<TabPanelProps>(({focusVisible}) => ({
    ...focusVisible && {
        boxShadow: 'inset 0 0 0 2px #ffffff, inset 0 0 0 4px #1b1b1b',
        outline: 'none'
    }
}));

export const TabPanel = React.forwardRef<any, TabPanelProps>(({
    children, onBlur, onFocus, ...props
}, ref) => {
    const {
        isFocusVisibleRef, onBlur: handleBlurVisible, onFocus: handleFocusVisible, ref: focusVisibleRef
    } = useIsFocusVisible();

    const [focusVisible, setFocusVisible] = React.useState(false);
    const handlerRef = useForkRef(ref, focusVisibleRef);

    const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
        handleBlurVisible(event);
        if (isFocusVisibleRef.current === false) {
            setFocusVisible(false);
        }
        if (onBlur) {
            onBlur(event);
        }
    };

    const handleFocus = (event: React.FocusEvent<HTMLDivElement>): void => {
        handleFocusVisible(event);
        if (isFocusVisibleRef.current === true && event.currentTarget === document.activeElement) {
            setFocusVisible(true);
        }
        if (onFocus) {
            onFocus(event);
        }
    };

    return (
        <StyledTabPanel
            focusVisible={focusVisible}
            onBlur={handleBlur}
            onFocus={handleFocus}
            ref={handlerRef}
            role='tabpanel'
            tabIndex={0}
            {...props}
        >
            {children}
        </StyledTabPanel>
    );
});

TabPanel.displayName = 'TabPanel';
